import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock, Copy, ExternalLink, Sparkles } from "lucide-react";
import { Sheet } from "@/components/Sheet";
import { Button, Card } from "@/components/ui";
import { useToast } from "@/components/Toast";
import { api, ApiError } from "@/lib/api";
import { useAction } from "@/lib/queries";
import { ASSISTANTS, WEEKLY_JOBS_REQUEST, type AssistantInfo } from "@shared/assistants";

interface Connections {
  connectorUrl: string;
  connections: { clientId: string; name: string; connectedAt: number; canChange: boolean; seesKidNames: boolean; seesUsage: boolean }[];
}

/** Connect the parent's own assistant (Claude, ChatGPT, Grok, Muse…) to set the family up by conversation. */
export function ClaudeConnectorCard() {
  const toast = useToast();
  const [picking, setPicking] = useState(false);
  const [chosen, setChosen] = useState<AssistantInfo | null>(null);
  const { data } = useQuery<Connections, ApiError>({ queryKey: ["oauth-connections"], queryFn: () => api.get<Connections>("/oauth/connections") });
  const disconnect = useAction((clientId: string) => api.del(`/oauth/connections/${encodeURIComponent(clientId)}`), {
    success: "Disconnected",
  });
  if (!data) return null;

  async function copy(text: string, what: string) {
    try {
      await navigator.clipboard.writeText(text);
      toast.show(what, "success");
    } catch {
      toast.show("Couldn't copy: press and hold the text to copy it.", "error");
    }
  }

  function close() {
    setPicking(false);
    setChosen(null);
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-2xl bg-violet-soft text-violet">
          <Sparkles className="size-5" />
        </span>
        <div>
          <p className="font-extrabold">Set up and manage with your own AI (optional)</p>
          <p className="text-sm text-muted">
            Answer a few questions in a chat and your assistant configures everything for you. There's no AI in the app itself; this connects your
            own account, on your own plan, and you choose what it can see and do.
          </p>
        </div>
      </div>
      <Button block onClick={() => setPicking(true)}>
        <Sparkles className="size-4" /> Connect my LLM
      </Button>
      <div className="space-y-1 text-sm">
        <p className="font-bold">With “view and make changes”, your assistant can:</p>
        <ul className="list-disc space-y-0.5 pl-5 text-muted">
          <li>Change family settings: currency and its icon, payouts, verify or trust check-offs, negative balances and work-it-off</li>
          <li>Update each kid's emoji avatar and settings, screen-time caps and hours, responsibilities and paid jobs (including pausing them)</li>
          <li>
            Recommend age-appropriate tasks with fair prices. They wait in <b>Approvals</b> until you approve them, unless you tell it to add them
            directly
          </li>
        </ul>
        <p className="text-muted">
          Kids show up as Kid 1, Kid 2… unless you choose to share their first names when connecting. Weekly usage counts are also off unless you turn
          them on (reconnect to change either). It never sees your kids' interests as written, balances, notes or PINs, can't delete anything, and
          every change shows in Privacy &amp; security.
        </p>
      </div>
      {data.connections.map((c) => (
        <div key={c.clientId} className="space-y-2 border-t border-line pt-3 text-sm">
          <div className="flex items-center justify-between gap-2">
            <span>
              <b>{c.name}</b> is connected ({c.canChange ? "view and change" : "view only"}; {c.seesKidNames ? "sees kids' first names" : "kids' names hidden"};{" "}
              {c.seesUsage ? "sees weekly usage counts" : "usage counts hidden"})
            </span>
            <Button size="sm" variant="ghost" className="text-danger" onClick={() => disconnect.mutate(c.clientId)} loading={disconnect.isPending}>
              Disconnect
            </Button>
          </div>
          <div className="flex items-start gap-2 rounded-2xl bg-sunken p-3">
            <CalendarClock className="mt-0.5 size-4 shrink-0 text-violet" />
            <div className="min-w-0 flex-1 space-y-2">
              <p>
                <b>Want job ideas every week?</b> Ask {c.name} to do it. Ideas grow with your kids' ages and wait for your approval. Scheduling happens
                in {c.name}, on your plan, if it offers scheduling.
              </p>
              <Button size="sm" variant="secondary" onClick={() => copy(WEEKLY_JOBS_REQUEST, "Request copied. Paste it into your assistant.")}>
                <Copy className="size-4" /> Copy the request
              </Button>
            </div>
          </div>
        </div>
      ))}

      <Sheet open={picking} onClose={close} title={chosen ? `Connect ${chosen.name}` : "Which assistant do you use?"}>
        {chosen ? (
          <div className="space-y-4">
            {chosen.note && <p className="rounded-2xl bg-coin-soft p-3 text-sm">{chosen.note}</p>}
            <div className="space-y-1 text-sm">
              <p className="font-bold">1. Copy this address</p>
              <div className="flex items-center gap-2 rounded-2xl bg-sunken px-3 py-2">
                <code className="min-w-0 flex-1 truncate">{data.connectorUrl}</code>
                <Button size="sm" variant="secondary" onClick={() => copy(data.connectorUrl, "Address copied")}>
                  <Copy className="size-4" /> Copy
                </Button>
              </div>
            </div>
            <div className="space-y-1 text-sm">
              <p className="font-bold">2. In {chosen.name}</p>
              <ol className="list-decimal space-y-1 pl-5">
                {chosen.steps.map((s) => (
                  <li key={s}>{s}</li>
                ))}
              </ol>
            </div>
            <a
              href={chosen.openUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => void copy(data.connectorUrl, "Address copied")}
              className="flex items-center justify-center gap-2 rounded-2xl bg-brand px-4 py-3 font-extrabold text-white"
            >
              Copy address and open {chosen.name} <ExternalLink className="size-4" />
            </a>
            <Button block variant="ghost" onClick={() => setChosen(null)}>
              Choose a different assistant
            </Button>
          </div>
        ) : (
          <div className="grid gap-2">
            {ASSISTANTS.map((a) => (
              <button key={a.id} type="button" onClick={() => setChosen(a)} className="rounded-2xl bg-card p-3 text-left shadow-card ring-1 ring-line">
                <span className="block font-extrabold">{a.name}</span>
                <span className="block text-sm text-muted">by {a.company}</span>
              </button>
            ))}
            <p className="pt-1 text-sm text-muted">Using something else? It can connect if it runs on your own computer: paste this address into it.</p>
            <Button variant="secondary" onClick={() => copy(data.connectorUrl, "Address copied")}>
              <Copy className="size-4" /> Copy the address
            </Button>
          </div>
        )}
      </Sheet>
    </Card>
  );
}
